import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer,
  Tooltip, XAxis, YAxis,
} from 'recharts';
import type { TransitReport } from '@/lib/adminApi';

/** Labels for the map filters riders tap, matching the passenger app. */
const INTEREST_LABELS: Record<string, string> = {
  eat: 'Eat & Drink',
  shop: 'Shop',
  fuel: 'Fuel',
  money: 'Banks & ATMs',
  care: 'Health & Care',
  stay: 'Places to stay',
  explore: 'Tours & attractions',
};

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
/** Postgres DOW (0 = Sunday) for each row of DAYS. */
const DAY_DOW = [1, 2, 3, 4, 5, 6, 0];

const COLOR_COMPLETED = '#10b981';
const COLOR_UNMET = '#f59e0b';
const COLOR_REQUESTED = '#06b6d4';
const COLOR_OTHER = '#6b7280';

interface Theme {
  card: string;
  title: string;
  text: string;
  muted: string;
  tile: string;
  rowBorder: string;
  grid: string;
  axis: string;
  heatEmpty: string;
  tooltip: React.CSSProperties;
}

const SCREEN: Theme = {
  card: 'bg-gray-900/50 border border-gray-800 rounded-xl',
  title: 'text-white',
  text: 'text-gray-300',
  muted: 'text-gray-500',
  tile: 'bg-gray-900/50 border border-gray-800 rounded-xl',
  rowBorder: 'border-gray-800',
  grid: '#374151',
  axis: '#9ca3af',
  heatEmpty: 'rgba(55, 65, 81, 0.35)',
  tooltip: { backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: 8, color: '#fff' },
};

const PAPER: Theme = {
  card: 'bg-white border border-gray-200 rounded-lg break-inside-avoid',
  title: 'text-gray-900',
  text: 'text-gray-700',
  muted: 'text-gray-500',
  tile: 'bg-gray-50 border border-gray-200 rounded-lg',
  rowBorder: 'border-gray-200',
  grid: '#e5e7eb',
  axis: '#6b7280',
  heatEmpty: '#f3f4f6',
  tooltip: { backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, color: '#111' },
};

function fmtNumber(n: number | null | undefined) {
  return n == null ? '–' : n.toLocaleString();
}

function fmtXcd(n: number | null | undefined) {
  if (n == null) return '–';
  return `EC$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtMinutes(n: number | null | undefined) {
  if (n == null) return '–';
  return n < 1 ? 'under 1 min' : `${n.toLocaleString(undefined, { maximumFractionDigits: 1 })} min`;
}

function fmtPercent(n: number | null | undefined) {
  return n == null ? '–' : `${Math.round(n * 100)}%`;
}

function fmtDay(iso: string, withYear = false) {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    ...(withYear ? { year: 'numeric' } : {}),
  });
}

function hourLabel(h: number) {
  if (h === 0) return '12am';
  if (h === 12) return '12pm';
  return h < 12 ? `${h}am` : `${h - 12}pm`;
}

/** First and last day the report covers, in Dominica time. */
export function reportPeriod(report: TransitReport) {
  const first = report.daily[0]?.date;
  const last = report.daily[report.daily.length - 1]?.date;
  if (!first || !last) return '';
  return `${fmtDay(first, true)} to ${fmtDay(last, true)}`;
}

function Section({
  theme, title, subtitle, children, className = '',
}: {
  theme: Theme;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section className={`${theme.card} p-5 ${className}`}>
      <h2 className={`text-sm font-semibold ${theme.title}`}>{title}</h2>
      {subtitle && <p className={`text-xs mt-0.5 ${theme.muted}`}>{subtitle}</p>}
      <div className="mt-4">{children}</div>
    </section>
  );
}

function Empty({ theme, children }: { theme: Theme; children: React.ReactNode }) {
  return (
    <div className={`h-40 flex items-center justify-center text-center text-sm px-6 ${theme.muted}`}>
      {children}
    </div>
  );
}

/**
 * The government transit report, drawn either for the dark admin screen or
 * as a white printable document (`paper`). Both read the same aggregated
 * figures, so the printout always matches what the admin sees.
 */
export default function TransitReportView({ report, paper = false }: { report: TransitReport; paper?: boolean }) {
  const theme = paper ? PAPER : SCREEN;
  const s = report.summary;
  const hasTrips = s.requested > 0;
  const chartAnimation = !paper;

  const kpis = [
    { label: 'Trips completed', value: fmtNumber(s.completed), note: `${fmtNumber(s.requested)} requested` },
    { label: 'Passengers carried', value: fmtNumber(s.passengers_carried), note: 'seats on completed trips' },
    { label: 'Requests a bus accepted', value: fmtPercent(s.fulfilment_rate), note: 'share of all requests' },
    { label: 'Requests no bus took', value: fmtNumber(s.unmet), note: 'riders left waiting' },
    { label: 'Median wait for a bus', value: fmtMinutes(s.median_wait_min), note: 'request to acceptance' },
    { label: 'Median pickup time', value: fmtMinutes(s.median_pickup_min), note: 'acceptance to boarding' },
    { label: 'Fare value', value: fmtXcd(s.fare_value_xcd), note: 'at the official fare' },
    {
      label: 'Buses in service',
      value: fmtNumber(s.active_buses),
      note: s.unique_riders == null ? 'riders: fewer than 5' : `${fmtNumber(s.unique_riders)} riders`,
    },
  ];

  const servedCorridors = report.corridors.filter((c) => c.requested > 0);
  const quietCorridors = report.corridors.filter((c) => c.requested === 0);
  const corridorChart = servedCorridors.map((c) => ({
    name: c.name,
    Completed: c.completed,
    'No bus took it': c.unmet,
    'Cancelled or other': Math.max(0, c.requested - c.completed - c.unmet),
  }));

  const heat = new Map<string, number>();
  for (const cell of report.heatmap) heat.set(`${cell.dow}-${cell.hour}`, cell.requests);
  const heatMax = Math.max(1, ...report.heatmap.map((c) => c.requests));
  const busiest = [...report.heatmap].sort((a, b) => b.requests - a.requests)[0];

  const interest = report.interest
    .filter((i) => INTEREST_LABELS[i.filter_id])
    .map((i) => ({ name: INTEREST_LABELS[i.filter_id], Searches: i.count }));

  const dailyTicks = report.daily.length > 31 ? Math.ceil(report.daily.length / 12) : report.days > 14 ? 2 : 0;

  const hazardTotal = report.community.hazards.reduce((sum, h) => sum + h.count, 0);
  const utilityCount = (utility: string, kind: string) =>
    report.community.utility_reports.find((u) => u.utility === utility && u.kind === kind)?.count ?? 0;

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {kpis.map((k) => (
          <div key={k.label} className={`${theme.tile} p-4`}>
            <p className={`text-[11px] uppercase tracking-wider ${theme.muted}`}>{k.label}</p>
            <p className={`text-2xl font-bold mt-1 ${theme.title}`}>{k.value}</p>
            <p className={`text-xs mt-0.5 ${theme.muted}`}>{k.note}</p>
          </div>
        ))}
      </div>

      <Section theme={theme} title="Demand and service, day by day" subtitle="Trips requested, completed, and requests no bus took">
        {!hasTrips ? (
          <Empty theme={theme}>No trips were requested in this period.</Empty>
        ) : (
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={report.daily} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
              <defs>
                <linearGradient id={`fillCompleted${paper ? 'P' : 'S'}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={COLOR_COMPLETED} stopOpacity={0.45} />
                  <stop offset="100%" stopColor={COLOR_COMPLETED} stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={theme.grid} vertical={false} />
              <XAxis
                dataKey="date"
                tickFormatter={(d) => fmtDay(d)}
                interval={dailyTicks}
                tick={{ fill: theme.axis, fontSize: 11 }}
                tickLine={false}
                axisLine={{ stroke: theme.grid }}
              />
              <YAxis allowDecimals={false} tick={{ fill: theme.axis, fontSize: 11 }} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={theme.tooltip} labelFormatter={(d) => fmtDay(String(d), true)} />
              <Legend wrapperStyle={{ fontSize: 12, color: theme.axis }} />
              <Area type="monotone" dataKey="requested" name="Requested" stroke={COLOR_REQUESTED} fill="none" strokeWidth={1.5} strokeDasharray="4 3" isAnimationActive={chartAnimation} />
              <Area type="monotone" dataKey="completed" name="Completed" stroke={COLOR_COMPLETED} fill={`url(#fillCompleted${paper ? 'P' : 'S'})`} strokeWidth={2} isAnimationActive={chartAnimation} />
              <Area type="monotone" dataKey="unmet" name="No bus took it" stroke={COLOR_UNMET} fill="none" strokeWidth={1.5} isAnimationActive={chartAnimation} />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </Section>

      <Section theme={theme} title="Corridor performance" subtitle="How each bus corridor served the riders who asked for it">
        {servedCorridors.length === 0 ? (
          <Empty theme={theme}>No corridor had a trip request in this period.</Empty>
        ) : (
          <>
            <ResponsiveContainer width="100%" height={Math.max(140, servedCorridors.length * 38 + 50)}>
              <BarChart data={corridorChart} layout="vertical" margin={{ top: 0, right: 16, left: 10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={theme.grid} horizontal={false} />
                <XAxis type="number" allowDecimals={false} tick={{ fill: theme.axis, fontSize: 11 }} tickLine={false} axisLine={false} />
                <YAxis type="category" dataKey="name" width={120} tick={{ fill: theme.axis, fontSize: 12 }} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={theme.tooltip} cursor={{ fill: paper ? '#f9fafb' : 'rgba(255,255,255,0.04)' }} />
                <Legend wrapperStyle={{ fontSize: 12, color: theme.axis }} />
                <Bar dataKey="Completed" stackId="a" fill={COLOR_COMPLETED} isAnimationActive={chartAnimation} />
                <Bar dataKey="No bus took it" stackId="a" fill={COLOR_UNMET} isAnimationActive={chartAnimation} />
                <Bar dataKey="Cancelled or other" stackId="a" fill={COLOR_OTHER} radius={[0, 4, 4, 0]} isAnimationActive={chartAnimation} />
              </BarChart>
            </ResponsiveContainer>

            <div className="overflow-x-auto mt-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className={`text-left text-[11px] uppercase tracking-wider ${theme.muted}`}>
                    <th className="py-2 pr-3 font-medium">Corridor</th>
                    <th className="py-2 px-3 font-medium text-right">Requests</th>
                    <th className="py-2 px-3 font-medium text-right">Completed</th>
                    <th className="py-2 px-3 font-medium text-right">Accepted</th>
                    <th className="py-2 px-3 font-medium text-right">Median wait</th>
                    <th className="py-2 px-3 font-medium text-right">Passengers</th>
                    <th className="py-2 pl-3 font-medium text-right">Fare value</th>
                  </tr>
                </thead>
                <tbody>
                  {servedCorridors.map((c) => (
                    <tr key={c.corridor_id} className={`border-t ${theme.rowBorder} ${theme.text}`}>
                      <td className={`py-2 pr-3 font-medium ${theme.title}`}>{c.name}</td>
                      <td className="py-2 px-3 text-right tabular-nums">{fmtNumber(c.requested)}</td>
                      <td className="py-2 px-3 text-right tabular-nums">{fmtNumber(c.completed)}</td>
                      <td className="py-2 px-3 text-right tabular-nums">
                        {fmtPercent(c.requested ? (c.requested - c.unmet) / c.requested : null)}
                      </td>
                      <td className="py-2 px-3 text-right tabular-nums">{fmtMinutes(c.median_wait_min)}</td>
                      <td className="py-2 px-3 text-right tabular-nums">{fmtNumber(c.passengers)}</td>
                      <td className="py-2 pl-3 text-right tabular-nums">{fmtXcd(c.fare_value_xcd)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
        {quietCorridors.length > 0 && (
          <p className={`text-xs mt-4 ${theme.muted}`}>
            No requests this period: {quietCorridors.map((c) => c.name).join(', ')}.
          </p>
        )}
      </Section>

      <Section
        theme={theme}
        title="When people need a bus"
        subtitle={
          busiest
            ? `Trip requests by day and hour, Dominica time. Busiest: ${DAYS[DAY_DOW.indexOf(busiest.dow)]} around ${hourLabel(busiest.hour)}.`
            : 'Trip requests by day and hour, Dominica time'
        }
      >
        {!hasTrips ? (
          <Empty theme={theme}>No trips were requested in this period.</Empty>
        ) : (
          <div className="overflow-x-auto">
            <div className="min-w-[620px]">
              <div className="grid gap-[3px]" style={{ gridTemplateColumns: '40px repeat(24, minmax(0, 1fr))' }}>
                <div />
                {Array.from({ length: 24 }, (_, h) => (
                  <div key={h} className={`text-[10px] text-center ${theme.muted}`}>
                    {h % 3 === 0 ? hourLabel(h) : ''}
                  </div>
                ))}
                {DAYS.map((day, row) => (
                  <div key={day} className="contents">
                    <div className={`text-[11px] flex items-center ${theme.muted}`}>{day}</div>
                    {Array.from({ length: 24 }, (_, h) => {
                      const n = heat.get(`${DAY_DOW[row]}-${h}`) ?? 0;
                      const strength = n === 0 ? 0 : 0.18 + 0.82 * (n / heatMax);
                      return (
                        <div
                          key={h}
                          title={`${day} ${hourLabel(h)}: ${n} request${n === 1 ? '' : 's'}`}
                          className="h-6 rounded-[3px]"
                          style={{
                            backgroundColor: n === 0 ? theme.heatEmpty : `rgba(16, 185, 129, ${strength.toFixed(2)})`,
                            WebkitPrintColorAdjust: 'exact',
                            printColorAdjust: 'exact',
                          }}
                        />
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </Section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Section theme={theme} title="Most requested journeys" subtitle={`Between named places, listed once seen at least ${report.min_cell} times`}>
          {report.journeys.top.length === 0 ? (
            <Empty theme={theme}>
              {hasTrips
                ? `No single journey reached ${report.min_cell} requests yet, so none are listed.`
                : 'No trips were requested in this period.'}
            </Empty>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className={`text-left text-[11px] uppercase tracking-wider ${theme.muted}`}>
                  <th className="py-2 pr-3 font-medium">Journey</th>
                  <th className="py-2 px-3 font-medium text-right">Requests</th>
                  <th className="py-2 pl-3 font-medium text-right">Completed</th>
                </tr>
              </thead>
              <tbody>
                {report.journeys.top.map((j) => (
                  <tr key={`${j.origin}-${j.destination}`} className={`border-t ${theme.rowBorder} ${theme.text}`}>
                    <td className={`py-2 pr-3 ${theme.title}`}>
                      {j.origin} <span className={theme.muted}>to</span> {j.destination}
                    </td>
                    <td className="py-2 px-3 text-right tabular-nums">{fmtNumber(j.requested)}</td>
                    <td className="py-2 pl-3 text-right tabular-nums">{fmtNumber(j.completed)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {report.journeys.other_requested > 0 && (
            <p className={`text-xs mt-3 ${theme.muted}`}>
              {fmtNumber(report.journeys.other_requested)} more requests were on journeys seen fewer than {report.min_cell} times. They are counted in the totals but not listed.
            </p>
          )}
        </Section>

        <Section theme={theme} title="What riders look for nearby" subtitle="Category searches on the rider map, a signal of local spending and visitor needs">
          {interest.length === 0 ? (
            <Empty theme={theme}>No category searches in this period.</Empty>
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(140, interest.length * 34 + 20)}>
              <BarChart data={interest} layout="vertical" margin={{ top: 0, right: 16, left: 10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={theme.grid} horizontal={false} />
                <XAxis type="number" allowDecimals={false} tick={{ fill: theme.axis, fontSize: 11 }} tickLine={false} axisLine={false} />
                <YAxis type="category" dataKey="name" width={130} tick={{ fill: theme.axis, fontSize: 12 }} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={theme.tooltip} cursor={{ fill: paper ? '#f9fafb' : 'rgba(255,255,255,0.04)' }} />
                <Bar dataKey="Searches" fill={COLOR_REQUESTED} radius={[0, 4, 4, 0]} isAnimationActive={chartAnimation} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Section>
      </div>

      <Section theme={theme} title="Road and utility reports from riders" subtitle="Reports riders sent from the app in this period">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <div className={`${theme.tile} p-4`}>
            <p className={`text-[11px] uppercase tracking-wider ${theme.muted}`}>Road hazards</p>
            <p className={`text-2xl font-bold mt-1 ${theme.title}`}>{fmtNumber(hazardTotal)}</p>
            <p className={`text-xs mt-0.5 ${theme.muted}`}>
              {report.community.hazards.length
                ? report.community.hazards.map((h) => `${h.type} ${h.count}`).join(' · ')
                : 'none reported'}
            </p>
          </div>
          <div className={`${theme.tile} p-4`}>
            <p className={`text-[11px] uppercase tracking-wider ${theme.muted}`}>Power outages</p>
            <p className={`text-2xl font-bold mt-1 ${theme.title}`}>{fmtNumber(utilityCount('power', 'out'))}</p>
            <p className={`text-xs mt-0.5 ${theme.muted}`}>{fmtNumber(utilityCount('power', 'restored'))} restored reports</p>
          </div>
          <div className={`${theme.tile} p-4`}>
            <p className={`text-[11px] uppercase tracking-wider ${theme.muted}`}>Water outages</p>
            <p className={`text-2xl font-bold mt-1 ${theme.title}`}>{fmtNumber(utilityCount('water', 'out'))}</p>
            <p className={`text-xs mt-0.5 ${theme.muted}`}>{fmtNumber(utilityCount('water', 'restored'))} restored reports</p>
          </div>
          <div className={`${theme.tile} p-4`}>
            <p className={`text-[11px] uppercase tracking-wider ${theme.muted}`}>Safety alerts</p>
            <p className={`text-2xl font-bold mt-1 ${theme.title}`}>{fmtNumber(report.community.sos_events)}</p>
            <p className={`text-xs mt-0.5 ${theme.muted}`}>SOS raised from the app</p>
          </div>
        </div>
      </Section>

      <section className={`${theme.card} p-5`}>
        <h2 className={`text-sm font-semibold ${theme.title}`}>How these figures are made</h2>
        <ul className={`text-xs mt-2 space-y-1 list-disc pl-4 ${theme.text}`}>
          <li>Every figure is a total, share or median across many trips. No names, phone numbers, account details or exact positions are included.</li>
          <li>Journeys between two places are only listed once seen at least {report.min_cell} times, and the rider count is hidden below {report.min_cell}.</li>
          <li>Fare value uses the official fare for each trip. Times are in Dominica time ({report.timezone}).</li>
          <li>{report.include_team ? 'This view includes H-Plate team test activity.' : 'H-Plate team test activity is excluded.'}</li>
        </ul>
      </section>
    </div>
  );
}
